"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_EXPOSURE_EXPLANATIONS = exports.AppBaseCapabilityAssessmentSchema = exports.AppBasePageExposureAssessmentSchema = void 0;
exports.assessAppBaseCapability = assessAppBaseCapability;
const zod_1 = require("zod");
const app_access_vocabulary_1 = require("./app-access-vocabulary");
const app_base_contract_1 = require("./app-base-contract");
const explanationSchema = zod_1.z
    .object({
    label: zod_1.z.string(),
    reason: zod_1.z.string(),
    consequences: zod_1.z.string(),
})
    .strict();
const ExplanationI18nSchema = zod_1.z
    .object({ en: explanationSchema, de: explanationSchema })
    .strict();
/** One page of an App: whom its rights open it for and how it is delivered. */
exports.AppBasePageExposureAssessmentSchema = zod_1.z
    .object({
    routePath: zod_1.z.string().min(1),
    audiences: zod_1.z.array(app_access_vocabulary_1.AppAccessAudienceSchema),
    exposureProfile: app_base_contract_1.AppBaseExposureProfileSchema,
    explanation: ExplanationI18nSchema,
})
    .strict();
exports.AppBaseCapabilityAssessmentSchema = zod_1.z
    .object({
    appId: zod_1.z.string().optional(),
    title: zod_1.z.string(),
    pages: zod_1.z.array(exports.AppBasePageExposureAssessmentSchema),
})
    .strict();
/** Presentation of the delivery of a page, never a second classifier. */
exports.APP_BASE_EXPOSURE_EXPLANATIONS = {
    internalLive: {
        en: {
            label: 'Internal',
            reason: 'This app is for people in your workspace.',
            consequences: 'Workspace sign-in and document permissions apply. Content is not made public.',
        },
        de: {
            label: 'Intern',
            reason: 'Diese App ist für Personen in deinem Workspace bestimmt.',
            consequences: 'Workspace-Anmeldung und Dokumentrechte gelten. Inhalte werden nicht öffentlich.',
        },
    },
    publicStatic: {
        en: {
            label: 'Public · static',
            reason: 'Visitors read published content without submitting changes.',
            consequences: 'Anyone with the public link can read published content without signing in. Forms and other write actions are not available. Changes become public only after publishing.',
        },
        de: {
            label: 'Öffentlich · statisch',
            reason: 'Besucher lesen veröffentlichte Inhalte, ohne Änderungen einzusenden.',
            consequences: 'Jeder mit dem öffentlichen Link kann veröffentlichte Inhalte ohne Anmeldung lesen. Formulare und andere Schreibaktionen sind nicht verfügbar. Änderungen werden erst nach dem Veröffentlichen öffentlich.',
        },
    },
    publicInteractive: {
        en: {
            label: 'Public · interactive',
            reason: 'Visitors can submit data, for example through a form, without signing in.',
            consequences: 'Published content is publicly readable. Visitors can use explicitly allowed actions with the required consent; other data remains protected. Changes become public only after publishing.',
        },
        de: {
            label: 'Öffentlich · interaktiv',
            reason: 'Besucher können ohne Anmeldung Daten einsenden, zum Beispiel über ein Formular.',
            consequences: 'Veröffentlichte Inhalte sind öffentlich lesbar. Besucher dürfen ausdrücklich erlaubte Aktionen mit der nötigen Einwilligung ausführen; andere Daten bleiben geschützt. Änderungen werden erst nach dem Veröffentlichen öffentlich.',
        },
    },
    memberPortal: {
        en: {
            label: 'Portal',
            reason: 'This app has routes that require member sign-in.',
            consequences: 'Portal routes require sign-in. Existing member and data permissions continue to determine what each person can see. Switching type does not create accounts or grant access to other people’s data.',
        },
        de: {
            label: 'Portal',
            reason: 'Diese App enthält Seiten, die eine Mitglieder-Anmeldung benötigen.',
            consequences: 'Portalseiten erfordern eine Anmeldung. Bestehende Mitglieder- und Datenrechte bestimmen weiterhin, was jede Person sieht. Der Wechsel legt keine Konten an und gibt keine fremden Daten frei.',
        },
    },
};
/**
 * The type of an App as its pages decide it. The caller reads the audiences
 * and the profile of each page from the rights; this only presents them.
 */
function assessAppBaseCapability(input) {
    return exports.AppBaseCapabilityAssessmentSchema.parse({
        appId: input.appId,
        title: input.title,
        pages: input.pages.map(page => ({
            routePath: page.routePath,
            audiences: [...page.audiences],
            exposureProfile: page.exposureProfile,
            explanation: exports.APP_BASE_EXPOSURE_EXPLANATIONS[page.exposureProfile],
        })),
    });
}
